import React, { useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, FormControlLabel, IconButton, MenuItem, Stack, Switch, Tab, Tabs, TextField,
  Typography, useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import { COLORS } from "./constants";
import { commonInputProps, inputSx, primaryButtonSx, secondaryButtonSx } from "./styles";
import { CopyableField } from "./CopyableField";
import { SrtUrlBreakdown } from "./SrtUrlBreakdown";
import { QrCode } from "./QrCode";

// ─────────────────────────────────────────────────────────────────────────────
// The setup windows of the Field reporters page. Setting up is done once; the
// page itself stays a short desk of reporters (talk, Go live, Standby), and
// everything with an address or a passphrase opens here, one reporter at a time.
//
//   ReporterSetupDialog: Phone (the QR code) · vMix (the input fields) · Advanced
//   ProgrammeSetupDialog: vMix's one programme output for every reporter
//
// The setup QR is the Fennira Broadcast app's own import link: d = the camera
// address, n = the reporter's name, rf/rfch = the return feed. One scan sets up
// both; it carries the channel's passphrase, so it is built here in the browser.
// ─────────────────────────────────────────────────────────────────────────────

export const LAYOUT_LABELS = {
  "prog-left": "Programme left · director right",
  "dir-left":  "Director left · programme right",
  "stereo":    "Programme in stereo, director in both ears"
};

export function setupLink(config, name, layout) {
  const parts = [`v=1`, `d=${encodeURIComponent(config.cameraPushUrl)}`, `n=${encodeURIComponent(name)}`];
  if (config.returnUrl) parts.push(`rf=${encodeURIComponent(config.returnUrl)}`, `rfch=${encodeURIComponent(layout)}`);
  return `fennira://import?${parts.join("&")}`;
}

const sectionLabel = { color: COLORS.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", mb: 0.75 };
const noteSx = { color: COLORS.muted, fontSize: 12, lineHeight: 1.6 };

/** The frame both windows share: title, close, scrolling body, footer. */
function SetupFrame({ open, onClose, title, subtitle, tabs, footer, children }) {
  const phone = useMediaQuery("(max-width:600px)");
  return (
    <Dialog open={open} onClose={onClose} fullScreen={phone} maxWidth={false} aria-labelledby="setup-dialog-title"
      PaperProps={{
        sx: {
          bgcolor: COLORS.panel, backgroundImage: "none", color: COLORS.text, fontFamily: COLORS.fontSans,
          display: "flex", flexDirection: "column", overflow: "hidden",
          ...(phone ? { borderRadius: 0 } : {
            width: "min(600px, calc(100vw - 48px))", maxHeight: "calc(100vh - 48px)", m: 3,
            borderRadius: "20px", border: `1px solid ${COLORS.borderStrong}`, boxShadow: "0 24px 64px rgba(0,0,0,0.5)"
          })
        }
      }}
      sx={{ "& .MuiBackdrop-root": { bgcolor: "rgba(8,7,24,0.72)", backdropFilter: "blur(4px)" } }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ px: 2.5, pt: 2.25, pb: tabs ? 0.5 : 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography id="setup-dialog-title" sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 700, fontSize: 19, lineHeight: 1.25,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</Typography>
          {subtitle && <Typography sx={{ mt: 0.35, color: COLORS.muted, fontSize: 13 }}>{subtitle}</Typography>}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" sx={{ color: COLORS.muted, mt: -0.5, mr: -1 }}><CloseIcon /></IconButton>
      </Stack>
      {tabs}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2, borderTop: `1px solid ${COLORS.border}` }}>{children}</Box>
      {footer && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between"
          sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
          {footer}
        </Stack>
      )}
    </Dialog>
  );
}

const tabsSx = {
  px: 1.5, minHeight: 40,
  "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, color: COLORS.muted, minHeight: 40, px: 1.5 },
  "& .Mui-selected": { color: `${COLORS.cyan} !important` },
  "& .MuiTabs-indicator": { bgcolor: COLORS.cyan }
};

/** "New code" with the question first: it changes the phone's QR and the vMix input. */
function NewCodeButton({ question, onRenew, disabled }) {
  return (
    <Button size="small" startIcon={<RefreshIcon />} disabled={disabled} sx={secondaryButtonSx}
      onClick={() => { if (window.confirm(question)) onRenew(); }}>
      New code
    </Button>
  );
}

// ── One reporter ─────────────────────────────────────────────────────────────

export function ReporterSetupDialog({
  open, onClose, channel: ch, config, busy, onLoadConfig, onRenew, onPatch, copiedField, copyToClipboard
}) {
  const [tab, setTab] = useState("phone");
  useEffect(() => { if (open) setTab("phone"); }, [open]);
  if (!ch) return null;

  const n = ch.channel;
  const id = (s) => `ch${n}${s}`;
  const link = config ? setupLink(config, ch.name, ch.layout) : "";

  const renewQuestion = `New code for ${ch.name}? The reporter must scan the new QR code, and the vMix input needs the new passphrase.`
    + (ch.live ? " The live connection restarts." : "");

  let body;
  if (!config) {
    body = (
      <Stack spacing={1.5} alignItems="flex-start" sx={{ py: 2 }}>
        {busy ? (
          <Stack direction="row" spacing={1.25} alignItems="center">
            <CircularProgress size={18} sx={{ color: COLORS.cyan }} />
            <Typography sx={{ color: COLORS.textSoft, fontSize: 14 }}>Loading the QR code…</Typography>
          </Stack>
        ) : (
          <Button variant="contained" onClick={() => onLoadConfig(n)} sx={primaryButtonSx}>Show QR code</Button>
        )}
        <Typography sx={noteSx}>It contains this reporter's passphrase, so Fennira may ask you to confirm it's you first.</Typography>
      </Stack>
    );
  } else if (tab === "phone") {
    body = (
      <Stack spacing={1.5} alignItems="center" sx={{ textAlign: "center" }}>
        <Typography sx={{ color: COLORS.textSoft, fontSize: 14, lineHeight: 1.55, maxWidth: 440 }}>
          In the <strong>Fennira Broadcast</strong> app, scan this code. One scan sets up the camera and the return
          (what the reporter hears).
        </Typography>
        <QrCode text={link} size={280} label={`Setup QR code for ${ch.name}`} />
        <Button size="small" startIcon={<LinkIcon />} sx={{ ...secondaryButtonSx, alignSelf: "center" }}
          onClick={() => copyToClipboard(link, id("Link"))}>
          {copiedField === id("Link") ? "Copied" : "Copy setup link (to send by message)"}
        </Button>
        <Typography sx={{ ...noteSx, maxWidth: 440 }}>
          The code stays valid on standby and between lives. It contains the passphrase: don't show it on a screen
          that is being shared or recorded.
        </Typography>
      </Stack>
    );
  } else if (tab === "vmix") {
    body = (
      <Stack spacing={2}>
        <Typography sx={{ color: COLORS.textSoft, fontSize: 14, lineHeight: 1.55 }}>
          In vMix: <strong>Add Input → Stream / SRT</strong>, Type <strong>SRT (Caller)</strong>, then fill in these
          fields. Set it up once: it stays valid until you press New code.
        </Typography>
        <SrtUrlBreakdown plain url={config.userReceiveUrl} idPrefix={id("Recv")} accent={COLORS.cyan}
          heading="Fields for vMix" copiedField={copiedField} onCopy={copyToClipboard} passphraseNote="" />
        <CopyableField label="Passphrase" value={config.passphrase} fieldId={id("Pass")} copiedField={copiedField}
          onCopy={copyToClipboard} secret />
        <CopyableField label="Full address (for software that takes one link)" value={config.userReceiveUrl}
          fieldId={id("Recv")} copiedField={copiedField} onCopy={copyToClipboard} secret />
      </Stack>
    );
  } else {
    body = (
      <Stack spacing={2.25}>
        {config.returnUrl && (
          <Box>
            <Typography sx={sectionLabel}>WHAT THE REPORTER HEARS</Typography>
            <TextField {...commonInputProps} select value={ch.layout} onChange={(e) => onPatch(n, { layout: e.target.value })}
              SelectProps={{ inputProps: { "aria-label": `Which ear hears what, ${ch.name}` } }} sx={{ ...inputSx, mb: 1 }}>
              {Object.entries(LAYOUT_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Switch checked={ch.hearsOthers} onChange={(e) => onPatch(n, { hearsOthers: e.target.checked })} />}
              label={<Typography sx={{ color: COLORS.textSoft, fontSize: 13 }}>
                Hears the other live reporters (never their own voice)
              </Typography>}
              sx={{ display: "flex" }}
            />
          </Box>
        )}
        <Box>
          <Typography sx={sectionLabel}>OTHER CAMERA APPS (NOT FENNIRA BROADCAST)</Typography>
          <CopyableField label="Camera address" value={config.cameraPushUrl} fieldId={id("Push")} copiedField={copiedField}
            onCopy={copyToClipboard} secret />
          {config.returnUrl && (
            <Box sx={{ mt: 1 }}>
              <CopyableField label="Return address" value={config.returnUrl} fieldId={id("Ret")} copiedField={copiedField}
                onCopy={copyToClipboard} secret />
            </Box>
          )}
        </Box>
        {config.programmeUrl && (
          <Box>
            <Typography sx={sectionLabel}>OWN PROGRAMME FOR THIS REPORTER (RARELY NEEDED)</Typography>
            <CopyableField value={config.programmeUrl} fieldId={id("Prog")} copiedField={copiedField} onCopy={copyToClipboard} secret />
            <Typography sx={{ ...noteSx, mt: 0.75 }}>
              A separate vMix output for this reporter only; it replaces the shared programme while connected. Usually
              the shared programme is enough.
            </Typography>
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <SetupFrame open={open} onClose={onClose} title={`Set up ${ch.name}`}
      subtitle={ch.live ? "Live now" : "On standby: the QR code and vMix input stay valid"}
      tabs={config && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabsSx} variant="fullWidth">
          <Tab value="phone" label="Phone" />
          <Tab value="vmix" label="vMix" />
          <Tab value="advanced" label="Advanced" />
        </Tabs>
      )}
      footer={<>
        {config ? <NewCodeButton question={renewQuestion} onRenew={() => onRenew(n)} disabled={busy} /> : <span />}
        <Button variant="contained" onClick={onClose} sx={primaryButtonSx}>Done</Button>
      </>}>
      {body}
    </SetupFrame>
  );
}

// ── The shared programme from vMix ───────────────────────────────────────────

export function ProgrammeSetupDialog({ open, onClose, config, busy, onLoad, onRenew, copiedField, copyToClipboard }) {
  return (
    <SetupFrame open={open} onClose={onClose} title="vMix programme output"
      subtitle="One output for all reporters, set up once"
      footer={<>
        {config ? (
          <NewCodeButton question="New code for the programme input? vMix's SRT output needs the new passphrase."
            onRenew={onRenew} disabled={busy} />
        ) : <span />}
        <Button variant="contained" onClick={onClose} sx={primaryButtonSx}>Done</Button>
      </>}>
      <Stack spacing={2}>
        <Box component="ol" sx={{ m: 0, pl: 2.5, color: COLORS.textSoft, fontSize: 14, lineHeight: 1.7 }}>
          <li>In vMix: <strong>Settings → Outputs / NDI / SRT</strong>, pick a free output, <strong>SRT</strong>, Type <strong>Caller</strong>.</li>
          <li>Fill in the fields below.</li>
          <li>As its audio, choose a bus with the studio (presenters, music) and <strong>no field reporter</strong>.</li>
        </Box>
        <Typography sx={noteSx}>
          Fennira gives each reporter this programme plus the other live reporters, never their own voice.
        </Typography>
        {!config ? (
          <Stack spacing={1} alignItems="flex-start">
            <Button variant="contained" onClick={onLoad} disabled={busy} sx={primaryButtonSx}>
              {busy ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Show the fields"}
            </Button>
            <Typography sx={noteSx}>They contain a passphrase, so Fennira may ask you to confirm it's you first.</Typography>
          </Stack>
        ) : (
          <>
            <SrtUrlBreakdown plain url={config.programmeUrl} idPrefix="progShared" accent={COLORS.cyan}
              heading="Fields for vMix" copiedField={copiedField} onCopy={copyToClipboard} passphraseNote="" />
            <CopyableField label="Passphrase" value={config.passphrase} fieldId="progSharedPass" copiedField={copiedField}
              onCopy={copyToClipboard} secret />
            <CopyableField label="Full address (for software that takes one link)" value={config.programmeUrl}
              fieldId="progShared" copiedField={copiedField} onCopy={copyToClipboard} secret />
          </>
        )}
      </Stack>
    </SetupFrame>
  );
}
